import { FC, PropsWithChildren, ReactNode, createContext, useContext, useMemo } from 'react'

import { store } from '@editor/store/app'
import { Provider as ReduxProvider } from 'react-redux'

/**
 * The editor header (the per-page toolbar that used to live at
 * `app/editor/src/pages/projects/common/Navbar`). It is host-supplied: each app
 * composes its own header from the building blocks the package exports and passes
 * it into {@link EditorProvider}. Editor pages render it through {@link EditorHeaderSlot}.
 */
const EditorHeaderContext = createContext<ReactNode>(null)

export const useEditorHeader = (): ReactNode => useContext(EditorHeaderContext)

/**
 * Renders the host-supplied editor header at the point in the editor page tree
 * where the header belongs. Sits inside the host's router, so the header has full
 * access to router hooks, the editor store and every editor context.
 */
export const EditorHeaderSlot: FC = () => <>{useEditorHeader()}</>

type EditorChrome = {
  /** The top app/dashboard navbar. Host-supplied; rendered by the editor error page. */
  topNavbar: ReactNode
}

const EditorChromeContext = createContext<EditorChrome>({ topNavbar: null })

export const useEditorChrome = (): EditorChrome => useContext(EditorChromeContext)

type EditorLinks = {
  /**
   * Where the blueprint editor's プラン管理へ button sends the user when Markly is
   * not active. Host-supplied because plan management lives in a different place
   * per app: hosts that carry their own account settings pass an app path, hosts
   * that delegate to Markly pass an absolute URL and leave the app.
   *
   * Left unset, the button is not offered at all — a host that has nowhere to send
   * the user should not show one.
   */
  planManagementUrl?: string
}

const EditorLinksContext = createContext<EditorLinks>({})

export const useEditorLinks = (): EditorLinks => useContext(EditorLinksContext)

export type EditorProviderProps = PropsWithChildren<{
  /** The editor header, composed by the host from the package's building blocks. */
  header: ReactNode
  /**
   * The top app navbar (dashboard/auth chrome). Stays owned by the host app; the
   * package only renders it on the editor error page via {@link useEditorChrome}.
   */
  topNavbar?: ReactNode
  /** Plan management destination for the Markly-inactive gate. See {@link EditorLinks}. */
  planManagementUrl?: string
}>

/**
 * Wraps the shared editor wiring — the Redux store plus the header/chrome slots —
 * so a host app can mount the editor body without re-implementing it. The host
 * supplies its own header (and top navbar) and renders its own router as a child.
 */
export const EditorProvider: FC<EditorProviderProps> = ({ header, topNavbar = null, planManagementUrl, children }) => {
  const chrome = useMemo<EditorChrome>(() => ({ topNavbar }), [topNavbar])
  const links = useMemo<EditorLinks>(() => ({ planManagementUrl }), [planManagementUrl])

  return (
    <ReduxProvider store={store}>
      <EditorChromeContext.Provider value={chrome}>
        <EditorLinksContext.Provider value={links}>
          <EditorHeaderContext.Provider value={header}>{children}</EditorHeaderContext.Provider>
        </EditorLinksContext.Provider>
      </EditorChromeContext.Provider>
    </ReduxProvider>
  )
}

export default EditorProvider
